import { toast } from 'react-toastify';
import { postApiCall } from '../Handlers/HttpHandler';

/**
 * Fetches providers filtered by aggregatorId.
 * @param {string} token
 * @param {string|number} aggregatorId - required
 */
export const getProviders = async (token, aggregatorId) => {
  try {
    const body = aggregatorId ? { aggregatorId: Number(aggregatorId) } : {};
    const result = await postApiCall('provider-list', body, token);
    if (result.error) { toast.error(result.error?.message ?? result.error); return null; }
    return result.data ?? [];
  } catch {
    toast.error('Failed to load providers');
    return null;
  }
};

/**
 * Imports missing providers from an aggregator.
 * @param {string} token
 * @param {string|number} aggregatorId
 * @param {string[]|null} providerNames - null/empty = add all missing
 */
export const importMissingProviders = async (token, aggregatorId, providerNames = null) => {
  try {
    const body = { aggregatorId: Number(aggregatorId) };
    if (providerNames && providerNames.length > 0) {
      body.providerNames = providerNames;
    }
    const result = await postApiCall('provider-add-missing', body, token);
    if (result.error) { toast.error(result.error?.message ?? result.error); return null; }
    return result.data;
  } catch {
    toast.error('Failed to import providers');
    return null;
  }
};

export const getProviderAliases = async (token) => {
  try {
    const result = await postApiCall('provider-aliases-list', {}, token);
    if (result.error) { toast.error(result.error?.message ?? result.error); return null; }
    return result.data ?? {};
  } catch {
    toast.error('Failed to load provider aliases');
    return null;
  }
};

export const updateProviderAliases = async (token, aggregatorId, aliases) => {
  try {
    const result = await postApiCall('provider-aliases-update', { aggregatorId: Number(aggregatorId), aliases }, token);
    if (result.error) { toast.error(result.error?.message ?? result.error); return null; }
    return result.data;
  } catch {
    toast.error('Failed to save provider aliases');
    return null;
  }
};

export const getDbProviders = async (token, aggregatorId) => {
  try {
    const result = await postApiCall('provider-db-list', { aggregatorId: Number(aggregatorId) }, token);
    if (result.error) { toast.error(result.error?.message ?? result.error); return null; }
    return result.data ?? [];
  } catch {
    toast.error('Failed to load DB providers');
    return null;
  }
};

export const deleteProviderAlias = async (token, aggregatorId, apiName) => {
  try {
    const result = await postApiCall('provider-aliases-delete', { aggregatorId: Number(aggregatorId), apiName }, token);
    if (result.error) { toast.error(result.error?.message ?? result.error); return null; }
    return result.data;
  } catch {
    toast.error('Failed to delete alias');
    return null;
  }
};

/**
 * Fetches raw API-side provider names for an aggregator (no DB merge).
 * @param {string} token
 * @param {string|number} aggregatorId
 */
export const getApiProviderNames = async (token, aggregatorId) => {
  try {
    const result = await postApiCall('provider-api-list', { aggregatorId: Number(aggregatorId) }, token);
    if (result.error) { toast.error(result.error?.message ?? result.error); return null; }
    return result.data ?? [];
  } catch {
    toast.error('Failed to load API provider names');
    return null;
  }
};

/**
 * Finds DB providers similar to either the API name or the DB provider being imported to.
 * @param {string} token
 * @param {string|number} aggregatorId
 * @param {string|number|null} providerId - the DB provider being imported to (excluded from results)
 * @param {string} apiProviderName
 * @param {string|null} dbProviderName - resolved DB provider name, if known
 */
export const getSimilarProviderNames = async (token, aggregatorId, providerId, apiProviderName, dbProviderName) => {
  try {
      const result = await postApiCall('provider-similar-names', {
        aggregatorId: Number(aggregatorId),
        providerId: providerId ? Number(providerId) : null,
        apiProviderName,
        name: dbProviderName ?? null,
      }, token);
    if (result.error) { toast.error(result.error?.message ?? result.error); return null; }
    return result.data ?? [];
  } catch {
    toast.error('Failed to check similar provider names');
    return null;
  }
};